import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'

export interface PinReading {
  label: string; value: string; time: string; note: string; author: string; createdAt?: string
}

export function PinIcon() {
  return <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3h8l-1 6 4 4v2H5v-2l4-4-1-6ZM12 15v6" /></svg>
}

export function PinButton({ active, onClick }: { active: boolean; onClick: () => void }) {
  return <button type="button" className="button chart-tool chart-pin-button" aria-pressed={active} onClick={onClick}><PinIcon />Pin reading</button>
}

/** Both chart families confirm a snapped, real reading before saving. */
export function PinComposer({ reading, onSave, onCancel, disabled = false }: {
  reading: Pick<PinReading, 'label' | 'value' | 'time'>; onSave: (note: string) => void | Promise<void>; onCancel: () => void; disabled?: boolean
}) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const saving = useRef(false)
  // Heard on the document: the pointer may have left the note field for the chart.
  const cancel = useRef(onCancel)
  cancel.current = onCancel
  useEffect(() => {
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving.current) cancel.current() }
    document.addEventListener('keydown', escape)
    return () => document.removeEventListener('keydown', escape)
  }, [])
  return <form className="pin-composer" aria-label="Add reading pin" onSubmit={async event => {
    event.preventDefault(); if (saving.current || disabled) return
    saving.current = true; setBusy(true); setError('')
    try { await onSave(note.trim()) } catch (failed) { setError(failed instanceof Error ? failed.message : 'Could not save the pin. Try again.') }
    finally { saving.current = false; setBusy(false) }
  }}>
    <div className="pin-composer-reading"><span className="pin-composer-icon"><PinIcon /></span><div><strong>{reading.label}</strong><small>{reading.time}</small></div><b>{reading.value}</b></div>
    <label>Note <span className="muted">optional</span><input autoFocus aria-label="Pin note" maxLength={120} placeholder="What should others know about this reading?" value={note} disabled={busy} onChange={e => setNote(e.target.value)} /></label>
    {error && <p role="alert">{error}</p>}
    <div className="pin-composer-actions"><span>Saved with your name</span><button type="button" className="button" disabled={busy} onClick={onCancel}>Cancel</button><button type="submit" className="button primary" disabled={busy || disabled}>{busy ? 'Saving…' : 'Save pin'}</button></div>
  </form>
}

/** A small anchor, with details only on hover, keyboard focus or click.
 * The card is clamped inside ChartHost, in the same coordinates as ECharts. */
export function ChartPinMarker({ reading, number, x, y, width, height, color, onRemove }: {
  reading: PinReading; number: number; x: number; y: number; width: number; height: number; color: string; onRemove?: () => void | Promise<void>
}) {
  const [hover, setHover] = useState(false), [locked, setLocked] = useState(false), [focused, setFocused] = useState(false)
  const [busy, setBusy] = useState(false), [error, setError] = useState('')
  const ref = useRef<HTMLDivElement>(null), id = useId()
  const open = hover || locked || focused
  useEffect(() => {
    if (!locked) return
    const outside = (e: PointerEvent) => { if (e.target instanceof Node && !ref.current?.contains(e.target)) { setLocked(false); setFocused(false); setHover(false) } }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [locked])
  const cardWidth = Math.min(284, Math.max(100, width - 16))
  const preferredLeft = x + cardWidth + 28 <= width ? x + 20 : x - cardWidth - 20
  const left = Math.max(8, Math.min(width - cardWidth - 8, preferredLeft)) - x
  const cardTop = Math.max(8, Math.min(Math.max(8, height - 268), y - 50))
  return <div ref={ref} className={`reading-pin${open ? ' is-open' : ''}`} style={{ left:x, top:y, '--pin-color':color } as CSSProperties}
    onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
    onFocus={() => setFocused(true)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setFocused(false) }}
    onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setLocked(false); setHover(false); setFocused(false); ref.current?.querySelector('button')?.blur() } }}>
    <span className="reading-pin-dot" aria-hidden="true" />
    <button type="button" className="reading-pin-marker" aria-label={`Pin ${number}: ${reading.label}, ${reading.value}${reading.note ? `, ${reading.note}` : ''}`} aria-expanded={open} aria-controls={id}
      onClick={() => { if (locked) { setLocked(false); setFocused(false); setHover(false) } else setLocked(true) }}>{number}</button>
    {open && <div id={id} className="reading-pin-card" role="group" aria-label={`Pinned reading ${number}`} style={{ left, width:cardWidth, top:cardTop-y, maxHeight:height-cardTop-8 }}>
      <header><span><PinIcon />Pinned reading {number}</span><button type="button" aria-label="Close pin details" onClick={() => { setLocked(false); setHover(false); setFocused(false) }}>×</button></header>
      <strong>{reading.label}</strong><b className="reading-pin-value">{reading.value}</b><time>{reading.time}</time>
      {reading.note && <p>{reading.note}</p>}
      <footer><span>Pinned by <strong>{reading.author}</strong>{reading.createdAt && <small>{reading.createdAt}</small>}</span>{onRemove && <button type="button" disabled={busy} onClick={async () => {
        if (busy) return; setBusy(true); setError('')
        try { await onRemove() } catch (failed) { setError(failed instanceof Error ? failed.message : 'Could not remove pin.') } finally { setBusy(false) }
      }} aria-label={`Remove pin ${reading.note || number}`}>{busy ? 'Removing…' : 'Remove'}</button>}</footer>
      {error && <p role="alert">{error}</p>}
    </div>}
  </div>
}
